import React from "react";
import { PlayCircle, X, MapPin, ArrowUp } from "lucide-react";

/* Set your tutorial video link here (YouTube embed URL or mp4).
   e.g. "https://www.youtube.com/embed/XXXXXXXX" */
export const TUTORIAL_VIDEO_URL = "https://www.youtube.com/embed/g8iFk0UvSls";

const LISTING_STEPS = [
  "Upload image",
  "Enter Brand name, MRP, Selling price",
  "Click on Run AI Auto-Fill",
  "Pick sizes",
  "Add measurements (if fashion category)",
  "Generate Myntra Excel",
];

export function StepsSidebar() {
  return (
    <div
      className="hidden 2xl:block fixed left-60 top-28 w-56 z-30 rounded-2xl p-4 bg-white border-2 border-emerald-300 shadow-lg"
      data-testid="listing-steps-sidebar"
    >
      <div className="text-sm font-extrabold text-[#282C3F] mb-3">
        How to do Listings
      </div>
      <ol className="space-y-2.5">
        {LISTING_STEPS.map((step, i) => (
          <li key={i} className="flex items-start gap-2.5" data-testid={`listing-step-${i + 1}`}>
            <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] font-bold grid place-items-center shrink-0 mt-0.5">
              {i + 1}
            </span>
            <span className="text-[12px] text-[#535665] leading-snug font-medium">{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function TutorialSidebar({ onWatch, onOpenDefaults }) {
  return (
    <div
      className="hidden 2xl:flex flex-col gap-4 fixed right-6 top-28 w-64 z-30"
      data-testid="tutorial-sidebar"
    >
      {/* Card 1 — Watch tutorial video */}
      <button
        onClick={onWatch}
        className="text-left rounded-2xl p-4 bg-white border-2 border-[#4F46E5]/30 hover:border-[#4F46E5] shadow-lg transition-colors"
        data-testid="watch-tutorial-card"
      >
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-[#4F46E5]/10 grid place-items-center shrink-0">
            <PlayCircle size={22} className="text-[#4F46E5]" />
          </span>
          <div>
            <div className="text-sm font-extrabold text-[#282C3F]">Watch Tutorial Video</div>
            <div className="text-[11px] text-[#535665] leading-snug mt-0.5">
              Learn in 2 minutes — how to create AI Auto Listings (FREE demo)
            </div>
          </div>
        </div>
      </button>

      {/* Card 2 — One-time Defaults setup */}
      <div
        className="rounded-2xl p-4 bg-white border-2 border-amber-300 shadow-lg"
        data-testid="defaults-instruction-card"
      >
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-xl bg-amber-100 grid place-items-center shrink-0">
            <MapPin size={20} className="text-amber-600" />
          </span>
          <div>
            <div className="text-sm font-extrabold text-[#282C3F]">
              One-Time Setup <ArrowUp size={12} className="inline text-amber-600" />
            </div>
            <div className="text-[11px] text-[#535665] leading-snug mt-0.5">
              Fill your <b>address, HSN code, manufacturer</b> details in the{" "}
              <b>Defaults</b> section (top of page) — just once, and they
              auto-fill in every listing.
            </div>
            <button
              onClick={onOpenDefaults}
              className="mt-2 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold"
              data-testid="open-defaults-btn"
            >
              Open Defaults →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TutorialVideoModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center p-4"
      style={{ background: "rgba(15,23,42,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="tutorial-video-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#EDEDF1]">
          <div>
            <div className="font-extrabold text-[#282C3F]">How to do AI Auto Listings — FREE</div>
            <div className="text-[11px] text-[#94969F]">Photo upload → AI Auto-Fill → sizes → Myntra Excel, in just 2 minutes</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg border border-[#D4D5D9] hover:bg-[#F5F5F8] text-[#535665] text-xs font-bold"
              data-testid="tutorial-skip-btn"
            >
              Skip →
            </button>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#F5F5F8] text-[#535665]" data-testid="tutorial-close">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="aspect-video bg-black grid place-items-center">
          {TUTORIAL_VIDEO_URL ? (
            <iframe
              src={TUTORIAL_VIDEO_URL}
              title="AI Auto Listing tutorial"
              className="w-full h-full"
              allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="text-center px-8">
              <PlayCircle size={44} className="text-white/40 mx-auto mb-3" />
              <div className="text-white font-bold text-sm">Tutorial video coming soon</div>
              <div className="text-white/60 text-xs mt-2 leading-relaxed">
                Meanwhile: <b>1)</b> Upload product photo · <b>2)</b> Click Run AI Auto-Fill ·{" "}
                <b>3)</b> Enter Brand &amp; price · <b>4)</b> Pick sizes · <b>5)</b> Generate Excel — done!
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
